/**
 * ProductCreatePage — Product type aware creation form.
 * Step 0: Type selector (rental / service / for_sale)
 * Steps 1-6: Full form with type-conditional sections.
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Upload, X, Star, Check, Loader2,
  AlertCircle, Tag, ShoppingCart, Wrench, RotateCcw, Palette, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { createProduct, uploadProductImage, fetchCategories, fetchUnits, createVariant, uploadVariantImage } from '../../api/supplierApi';
import { apiClient } from '../../lib/apiClient';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'TRY'];
const SEL = "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300";

const LOGISTIC_COMPANY_CATEGORY_ID = 'cfd92a00-4082-4378-80fa-35c70b1259f7';
const FORKLIFT_PROVIDER_SUBCATEGORY_ID = 'd9ca85a6-d82d-410b-aadb-79e423ed897e';
const FORKLIFT_PRESETS = [
  { key: 'standard_1_5t', label: 'Standart Forklift 1.5 Ton' },
  { key: 'standard_2t',   label: 'Standart Forklift 2 Ton'   },
  { key: 'standard_3t',   label: 'Standart Forklift 3 Ton'   },
  { key: 'standard_5t',   label: 'Standart Forklift 5 Ton'   },
  { key: 'standard_7t',   label: 'Standart Forklift 7 Ton'   },
];

// PRODUCT_TEMPLATES sabit listesi kaldırıldı — şablonlar artık API'den dinamik olarak yükleniyor.

const STOCK_POLICIES = [
  { value: 'untracked', label: 'Takip Edilmiyor', desc: 'Stok sayısı izlenmez' },
  { value: 'strict', label: 'Katı Stok', desc: 'Stok bitince sipariş alınmaz' },
  { value: 'flexible_supply', label: 'Esnek Tedarik', desc: 'Stok bitince "temin edilebilir" mesajı gösterilir' },
];

const PRODUCT_TYPES_INFO = [
  {
    value: 'rental',
    label: 'Kiralık Ürün',
    desc: 'Stand, ekipman, mobilya — günlük veya etkinlik bazlı kiralama',
    Icon: RotateCcw,
    colorBg: 'bg-emerald-100',
    colorText: 'text-emerald-600',
    colorBorder: 'border-emerald-200 hover:border-emerald-400',
    colorHover: 'hover:bg-emerald-50',
    dot: 'bg-emerald-500',
  },
  {
    value: 'service',
    label: 'Hizmet',
    desc: 'Catering, teknik destek, nakliye — sabit veya kişi/adet bazlı fiyatlandırma',
    Icon: Wrench,
    colorBg: 'bg-purple-100',
    colorText: 'text-purple-600',
    colorBorder: 'border-purple-200 hover:border-purple-400',
    colorHover: 'hover:bg-purple-50',
    dot: 'bg-purple-500',
  },
  {
    value: 'for_sale',
    label: 'Satılık Ürün',
    desc: 'Birim fiyatıyla direkt satış — stok yönetimi dahil',
    Icon: ShoppingCart,
    colorBg: 'bg-blue-100',
    colorText: 'text-blue-600',
    colorBorder: 'border-blue-200 hover:border-blue-400',
    colorHover: 'hover:bg-blue-50',
    dot: 'bg-blue-500',
  },
];

const TYPE_BADGE = {
  rental:   { label: 'Kiralık',  cls: 'bg-emerald-100 text-emerald-700' },
  service:  { label: 'Hizmet',   cls: 'bg-purple-100 text-purple-700' },
  for_sale: { label: 'Satılık',  cls: 'bg-blue-100 text-blue-700' },
};

// ── Pending Image Item ─────────────────────────────────────────────────────
function PendingImage({ preview, isCover, onSetCover, onRemove }) {
  return (
    <div className="relative group h-24 w-24 shrink-0 rounded-xl overflow-hidden border-2 border-gray-100">
      <img src={preview} alt="" className="w-full h-full object-cover" />
      {isCover && (
        <span className="absolute top-1 left-1 bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
          <Star className="h-2.5 w-2.5" /> Kapak
        </span>
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        {!isCover && (
          <button onClick={onSetCover} title="Kapak yap" className="text-white hover:text-amber-300">
            <Star className="h-4 w-4" />
          </button>
        )}
        <button onClick={onRemove} className="text-white hover:text-red-400">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ── Upload Progress Bar ────────────────────────────────────────────────────
function UploadProgress({ current, total, percent, filename }) {
  return (
    <div className="bg-amber-50 rounded-2xl p-4 space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-amber-800">Yükleniyor: {filename}</span>
        <span className="text-amber-600">{current}/{total}</span>
      </div>
      <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
        <div className="h-full bg-amber-500 rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

// ── Section Header ─────────────────────────────────────────────────────────
function SectionHeader({ step, title, desc }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
      <div className="h-7 w-7 rounded-lg bg-amber-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{step}</div>
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

// ── Type Selector (Step 0) ─────────────────────────────────────────────────
function TypeSelector({ onSelect, onBack }) {
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ürün Tipi Seçin</h1>
          <p className="text-sm text-gray-400">Ürünü doğru şekilde tanımlamak için uygun tipi seçin</p>
        </div>
      </div>
      <div className="space-y-3">
        {PRODUCT_TYPES_INFO.map(({ value, label, desc, Icon, colorBg, colorText, colorBorder, colorHover, dot }) => (
          <button
            key={value}
            onClick={() => onSelect(value)}
            data-testid={`type-select-${value}`}
            className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 transition-all text-left bg-white ${colorBorder} ${colorHover}`}
          >
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${colorBg}`}>
              <Icon className={`h-6 w-6 ${colorText}`} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-base">{label}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{desc}</p>
            </div>
            <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function ProductCreatePage({ tr, onBack, onSaved }) {
  const [productType, setProductType] = useState(null);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [productProfiles, setProductProfiles] = useState([]);
  const [pendingImages, setPendingImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadState, setUploadState] = useState(null);
  const [errors, setErrors] = useState({});
  const [variants, setVariants] = useState([]);
  const [allPatternGroups, setAllPatternGroups] = useState([]); // all pattern groups loaded once
  const [availablePatternItems, setAvailablePatternItems] = useState([]); // items for selected template
  const [selectedPatternIds, setSelectedPatternIds] = useState(new Set()); // chosen for product
  const [openColorPicker, setOpenColorPicker] = useState(null); // variant index with open picker
  const variantFileRefs = useRef({});
  const dropRef = useRef(null);
  const fileRef = useRef(null);

  const isRental  = productType === 'rental';
  const isService = productType === 'service';
  const isForSale = productType === 'for_sale';

  const [form, setForm] = useState({
    title: '', title_tr: '', title_en: '', description: '', description_tr: '', description_en: '',
    main_category_id: '', subcategory_id: '', category: '',
    currency: 'USD',
    product_code: '',
    min_order_quantity: '', max_order_quantity: '', unit_id: '', unit: '',
    lead_time_days: '', is_active: true, requires_supplier_confirmation: false,
    min_rental_period_days: '',
    // Pricing fields
    pricing_mode_supported: 'both',
    daily_price: '', event_price: '',
    calculation_type: 'flat',
    service_pricing_type: 'fixed',
    items_per_carton: '',
    price_per_person_per_day: '', price_per_person_event: '',
    minimum_people: '', maximum_people: '',
    // Forklift fields
    forklift_type: '',
    forklift_tonnage: '',
    operating_hours_start: '08:00',
    operating_hours_end: '18:00',
    hourly_price: '',
  });

  const [stockPolicy, setStockPolicy] = useState('untracked');
  const [stockQuantity, setStockQuantity] = useState('');
  const [allowBackorder, setAllowBackorder] = useState(false);

  useEffect(() => {
    Promise.all([fetchCategories().catch(() => []), fetchUnits().catch(() => [])])
      .then(([cats, unitList]) => { setCategories(cats); setUnits(unitList); });
    apiClient.get('/supplier/product-profiles').then(r => setProductProfiles(r.data || [])).catch(() => {});
    // Load pattern groups (public) for color/pattern assignment
    apiClient.get('/ultra-admin/pattern-groups/public').then(r => setAllPatternGroups(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };

  const selectedCat = categories.find(c => c.id === form.main_category_id);
  const isHR = selectedCat?.name === 'Human Resources';
  const isForklift = form.subcategory_id === FORKLIFT_PROVIDER_SUBCATEGORY_ID;
  const subcategories = selectedCat?.subcategories || [];

  // Seçilen alt kategorinin ürün şablonları — tamamen API'den (hardcoded fallback yok)
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [appliedProfile, setAppliedProfile] = useState(null); // Şablon üzerinden uygulanan profil

  const templateOptions = useMemo(() => {
    if (isForklift || !form.subcategory_id) return [];
    const sub = (selectedCat?.subcategories || []).find(s => s.id === form.subcategory_id);
    return sub?.product_templates || [];
  }, [isForklift, form.subcategory_id, selectedCat]);

  // Alt kategori değişince şablon ve profil sıfırla
  useEffect(() => {
    setSelectedTemplate('');
    setAppliedProfile(null);
  }, [form.subcategory_id]);

  // Şablon seçilince ürün adını, profili ve pattern öğelerini otomatik uygula
  const applyTemplate = (val) => {
    setSelectedTemplate(val);
    setAvailablePatternItems([]);
    setSelectedPatternIds(new Set());
    if (!val || val === '__custom') {
      setAppliedProfile(null);
      return;
    }
    set('title', val);
    set('title_tr', val); // Şablon adını TR alana da doldur (kullanıcı değiştirebilir)
    // Profil atanmış mı kontrol et
    const sub = (selectedCat?.subcategories || []).find(s => s.id === form.subcategory_id);
    const profileId = (sub?.template_profile_map || {})[val];
    if (profileId) {
      const profile = productProfiles.find(p => p.id === profileId);
      if (profile) {
        if (profile.calculation_type) set('calculation_type', profile.calculation_type);
        if (profile.pricing_mode)     set('service_pricing_type', profile.pricing_mode);
        setAppliedProfile(profile);
        toast.success(tr
          ? `"${profile.name}" profili hesaplama tipini otomatik ayarladı`
          : `Profile "${profile.name}" auto-set the calculation type`);
      }
    } else {
      setAppliedProfile(null);
    }
    // Pattern items assigned to this template
    const itemIds = (sub?.template_pattern_map || {})[val] || [];
    if (itemIds.length > 0) {
      const items = allPatternGroups.flatMap(g => (g.items || []).filter(i => itemIds.includes(i.id)));
      setAvailablePatternItems(items);
    }
  };

  // HR kategorisi seçilince otomatik olarak "Kişi Başı" + "Kişi Bazlı" hesaplama tipine geç
  useEffect(() => {
    if (isHR) {
      if (form.service_pricing_type !== 'person') set('service_pricing_type', 'person');
      if (form.calculation_type !== 'person_based') set('calculation_type', 'person_based');
    }
  }, [isHR]); // eslint-disable-line

  // Forklift kategorisi seçilince stok politikasını zorla
  useEffect(() => {
    if (isForklift) {
      if (stockPolicy !== 'strict') setStockPolicy('strict');
      if (form.calculation_type !== 'flat') set('calculation_type', 'flat');
    }
  }, [isForklift]); // eslint-disable-line

  // Forklift tipi seçilince ürün adını otomatik doldur (değiştirilebilir)
  useEffect(() => {
    if (!isForklift || !form.forklift_type || form.forklift_type === 'custom') return;
    const preset = FORKLIFT_PRESETS.find(p => p.key === form.forklift_type);
    if (!preset) return;
    const allLabels = FORKLIFT_PRESETS.map(p => p.label);
    // Sadece boşsa veya başka bir preset label'ı yazılıysa otomatik doldur
    if (!form.title_tr || allLabels.includes(form.title_tr)) {
      set('title_tr', preset.label);
      set('title', preset.label);
    }
  }, [form.forklift_type]); // eslint-disable-line

  const addImages = useCallback((files) => {
    const valid = Array.from(files).filter(f => {
      if (!f.type.startsWith('image/')) { toast.error(tr ? `${f.name}: Görsel dosyası olmalı.` : `${f.name}: must be an image file.`); return false; }
      if (f.size > 10 * 1024 * 1024) { toast.error(tr ? `${f.name}: Maks. 10 MB.` : `${f.name}: max 10 MB.`); return false; }
      return true;
    });
    const space = 5 - pendingImages.length;
    if (valid.length > space) toast.warning(tr ? `En fazla ${space} görsel daha ekleyebilirsiniz.` : `You can add at most ${space} more image(s).`);
    const toAdd = valid.slice(0, space).map((file, i) => ({
      id: `pending_${Date.now()}_${i}`,
      file,
      preview: URL.createObjectURL(file),
      isCover: pendingImages.length === 0 && i === 0,
    }));
    setPendingImages(prev => [...prev, ...toAdd]);
  }, [pendingImages.length]);

  const handleDragOver = (e) => { e.preventDefault(); dropRef.current?.classList.add('border-amber-400', 'bg-amber-50'); };
  const handleDragLeave = () => { dropRef.current?.classList.remove('border-amber-400', 'bg-amber-50'); };
  const handleDrop = (e) => { e.preventDefault(); handleDragLeave(); addImages(e.dataTransfer.files); };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Ürün adı zorunludur.';
    if (isForklift) {
      if (!form.forklift_type) e.forklift = 'Forklift tipi seçilmelidir.';
      if (!form.hourly_price || isNaN(parseFloat(form.hourly_price))) e.pricing = 'Saatlik ücret girilmelidir.';
    } else if (isRental || isHR) {
      const hasDailyPrice = form.daily_price !== '' && !isNaN(parseFloat(form.daily_price));
      const hasEventPrice = form.event_price !== '' && !isNaN(parseFloat(form.event_price));
      if (!hasDailyPrice && !hasEventPrice) e.pricing = 'En az bir fiyat girilmelidir (Günlük veya Etkinlik).';
    } else {
      const hasPrice = form.daily_price !== '' && !isNaN(parseFloat(form.daily_price));
      if (!hasPrice) e.pricing = isForSale ? 'Satış fiyatı girilmelidir.' : 'Hizmet fiyatı girilmelidir.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const f = (v) => (v !== '' && !isNaN(parseFloat(v))) ? parseFloat(v) : null;
      const ii = (v) => (v !== '' && !isNaN(parseInt(v))) ? parseInt(v) : null;
      const hourlyPrice = isForklift ? f(form.hourly_price) : null;
      const dailyPrice  = isForklift ? null : f(form.daily_price);
      const eventPrice  = isForklift ? null : (isRental || isHR) ? f(form.event_price) : null;

      // Ensure calculation_type is compatible with product type
      let calcType = form.calculation_type || 'flat';
      if (!isRental && ['quantity_and_days', 'person_and_days'].includes(calcType)) {
        calcType = calcType === 'quantity_and_days' ? 'quantity_based' : 'person_based';
      }
      if (isForSale && calcType === 'person_based') calcType = 'quantity_based';
      if (isForklift) calcType = 'flat';

      const payload = {
        title: form.title_tr || form.title_en || form.title || '',
        title_tr: form.title_tr || '',
        title_en: form.title_en || '',
        description: form.description,
        description_tr: form.description_tr || '',
        description_en: form.description_en || '',
        category: form.category,
        main_category_id: form.main_category_id || null,
        subcategory_id: form.subcategory_id || null,
        currency: form.currency,
        product_code: form.product_code || undefined,
        min_order_quantity: ii(form.min_order_quantity),
        max_order_quantity: ii(form.max_order_quantity),
        lead_time_days: ii(form.lead_time_days),
        min_rental_period_days: isRental ? ii(form.min_rental_period_days) : null,
        unit_id: form.unit_id || null,
        is_active: form.is_active,
        requires_supplier_confirmation: !!form.requires_supplier_confirmation,
        product_type: isForklift ? 'rental' : productType,
        // Pricing
        pricing_mode_supported: isForklift ? 'hourly' : ((isRental || form.service_pricing_type === 'daily' || isHR) ? 'both' : 'daily'),
        daily_price: dailyPrice,
        event_price: eventPrice,
        hourly_price: hourlyPrice,
        calculation_type: calcType,
        items_per_carton: form.calculation_type === 'carton_based' ? ii(form.items_per_carton) : null,
        price_per_person_per_day: (isRental || isService) ? f(form.price_per_person_per_day) : null,
        price_per_person_event: (isRental || isHR) ? f(form.price_per_person_event) : null,
        minimum_people: ii(form.minimum_people),
        maximum_people: ii(form.maximum_people),
        price: hourlyPrice || dailyPrice || eventPrice || 0,
        pricing: { daily: dailyPrice, event_duration: eventPrice },
        stock_policy: stockPolicy,
        stock_quantity: stockPolicy !== 'untracked' && stockQuantity !== '' ? parseInt(stockQuantity) : null,
        allow_backorder: allowBackorder,
        // Forklift specific
        forklift_type: isForklift ? form.forklift_type : null,
        forklift_tonnage: (isForklift && form.forklift_type === 'custom') ? f(form.forklift_tonnage) : null,
        operating_hours_start: isForklift ? (form.operating_hours_start || '08:00') : null,
        operating_hours_end: isForklift ? (form.operating_hours_end || '18:00') : null,
        service_pricing_type: isForklift ? 'hourly' : (isService ? form.service_pricing_type : null),
      };
      const saved = await createProduct(payload);

      // Upload images
      if (pendingImages.length > 0) {
        const coverIdx = pendingImages.findIndex(i => i.isCover);
        const ordered = coverIdx >= 0
          ? [pendingImages[coverIdx], ...pendingImages.filter((_, i) => i !== coverIdx)]
          : pendingImages;
        for (let i = 0; i < ordered.length; i++) {
          const item = ordered[i];
          setUploadState({ current: i + 1, total: ordered.length, percent: 0, filename: item.file.name });
          try {
            await uploadProductImage(saved.id, item.file, pct => setUploadState(s => ({ ...s, percent: pct })));
          } catch {
            toast.warning(tr ? `${item.file.name} yüklenemedi.` : `${item.file.name} could not be uploaded.`);
          }
        }
      }

      // Create variants
      for (const v of variants) {
        if (!v.name?.trim()) continue;
        try {
          const createdV = await createVariant(saved.id, {
            name: v.name,
            price: v.daily_price ? parseFloat(v.daily_price) : null,
            price_override_daily: v.daily_price ? parseFloat(v.daily_price) : null,
            price_override_event: v.event_price ? parseFloat(v.event_price) : null,
            sku: v.sku || null,
            stock: v.stock ? parseInt(v.stock) : null,
            color_id: v.color_id || null,
          });
          if (v.image_file && createdV?.id) {
            await uploadVariantImage(createdV.id, saved.id, v.image_file).catch(() => {});
          }
        } catch { /* skip failed variants */ }
      }

      toast.success(tr ? 'Ürün oluşturuldu!' : 'Product created!');
      onSaved(saved.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Hata oluştu.' : 'An error occurred.'));
    } finally {
      setSaving(false);
      setUploadState(null);
    }
  };

  // Step 0: Type selection
  if (!productType) {
    return <TypeSelector onSelect={setProductType} onBack={onBack} />;
  }

  const typeBadge = TYPE_BADGE[productType];

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <button onClick={() => setProductType(null)} className="text-gray-400 hover:text-gray-700 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Yeni Ürün</h1>
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${typeBadge.cls}`}>
              {typeBadge.label}
            </span>
          </div>
          <p className="text-sm text-gray-400">Ürün bilgilerini doldurun</p>
        </div>
        <button onClick={onBack} className="text-xs text-gray-400 hover:text-gray-600 underline">
          İptal
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 divide-y divide-gray-50">

        {/* 1 - Category */}
        <div className="p-6 space-y-4">
          <SectionHeader step="1" title="Kategori" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Ana Kategori</Label>
              <select value={form.main_category_id} onChange={e => {
                const cat = categories.find(c => c.id === e.target.value);
                set('main_category_id', e.target.value);
                set('category', cat?.name || '');
                set('subcategory_id', '');
              }} className={SEL}>
                <option value="">—</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Alt Kategori</Label>
              <select
                value={form.subcategory_id}
                onChange={e => set('subcategory_id', e.target.value)}
                disabled={!form.main_category_id || !subcategories.length}
                className={`${SEL} disabled:opacity-40`}
              >
                <option value="">—</option>
                {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Ürün Şablonu — alt kategori seçilince göster (forklift hariç) */}
          {templateOptions.length > 0 && (
            <div className="space-y-1.5 pt-1">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Ürün Şablonu Seç</Label>
              <select
                value={selectedTemplate}
                onChange={e => applyTemplate(e.target.value)}
                className={SEL}
              >
                <option value="">— Şablon seç (opsiyonel)</option>
                {templateOptions.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
                <option value="__custom">Kendiniz Yazın</option>
              </select>
              {selectedTemplate && selectedTemplate !== '__custom' && (
                <p className="text-xs text-blue-500 bg-blue-50 px-3 py-1.5 rounded-lg">
                  Ürün adı <span className="font-semibold">"{selectedTemplate}"</span> olarak dolduruldu — aşağıdan değiştirebilirsiniz.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">
                Min. Sipariş{form.calculation_type === 'carton_based' ? ' (koli)' : ' (adet)'}
              </Label>
              <Input type="number" value={form.min_order_quantity}
                onChange={e => set('min_order_quantity', e.target.value)}
                className="rounded-xl" min="1"
                placeholder={form.calculation_type === 'carton_based' ? 'ör. 2' : 'ör. 1'} />
              {form.calculation_type === 'carton_based' && form.items_per_carton && form.min_order_quantity && (
                <p className="text-[11px] text-amber-600">
                  = {(parseInt(form.min_order_quantity) || 0) * (parseInt(form.items_per_carton) || 1)} adet
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">
                Maks. Sipariş{form.calculation_type === 'carton_based' ? ' (koli)' : ' (adet)'}
              </Label>
              <Input type="number" value={form.max_order_quantity}
                onChange={e => set('max_order_quantity', e.target.value)}
                className="rounded-xl" min="1"
                placeholder="— (sınırsız)" />
              {form.calculation_type === 'carton_based' && form.items_per_carton && form.max_order_quantity && (
                <p className="text-[11px] text-amber-600">
                  = {(parseInt(form.max_order_quantity) || 0) * (parseInt(form.items_per_carton) || 1)} adet
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Birim</Label>
              <select value={form.unit_id} onChange={e => {
                const u = units.find(u => u.id === e.target.value);
                set('unit_id', e.target.value); set('unit', u?.code || '');
              }} className={SEL}>
                <option value="">—</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* 2.5 - Forklift Tipi (Logistic Company kategorisinde gösterilir) */}
        {isForklift && (
          <div className="p-6 space-y-4 bg-blue-50/40 border-l-4 border-blue-400">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-lg uppercase tracking-wide">Forklift Tipi</span>
              <span className="text-xs text-gray-400">Sadece bir tip seçilebilir</span>
            </div>
            {errors.forklift && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                <AlertCircle className="h-4 w-4 shrink-0" />{errors.forklift}
              </div>
            )}
            <div className="space-y-2">
              {FORKLIFT_PRESETS.map(preset => (
                <button key={preset.key} type="button"
                  data-testid={`forklift-type-${preset.key}`}
                  onClick={() => { set('forklift_type', preset.key); set('forklift_tonnage', ''); }}
                  className={`w-full flex items-center justify-between rounded-xl px-4 py-2.5 text-sm border-2 transition-all ${
                    form.forklift_type === preset.key
                      ? 'border-blue-500 bg-blue-100 text-blue-800 font-semibold'
                      : 'border-gray-100 bg-white text-gray-700 hover:border-blue-200'
                  }`}>
                  {preset.label}
                  {form.forklift_type === preset.key && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
                </button>
              ))}
              <button type="button"
                data-testid="forklift-type-custom"
                onClick={() => set('forklift_type', 'custom')}
                className={`w-full flex items-center justify-between rounded-xl px-4 py-2.5 text-sm border-2 transition-all ${
                  form.forklift_type === 'custom'
                    ? 'border-blue-500 bg-blue-100 text-blue-800 font-semibold'
                    : 'border-gray-100 bg-white text-gray-700 hover:border-blue-200'
                }`}>
                Özel Tonaj (Kendiniz Girin)
                {form.forklift_type === 'custom' && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
              </button>
            </div>
            {form.forklift_type === 'custom' && (
              <div className="space-y-2">
                <Label className="text-xs text-gray-500 uppercase">Tonaj</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="number" step="0.5" min="0.5" value={form.forklift_tonnage}
                    onChange={e => set('forklift_tonnage', e.target.value)}
                    data-testid="forklift-tonnage-input"
                    className="rounded-xl max-w-[140px]" placeholder="ör. 10" />
                  <span className="text-sm text-gray-400">Ton</span>
                  <button type="button"
                    data-testid="apply-custom-tonnage-btn"
                    onClick={() => {
                      if (!form.forklift_tonnage) return;
                      const label = tr ? `Özel Forklift ${form.forklift_tonnage} Ton` : `Custom Forklift ${form.forklift_tonnage} Ton`;
                      set('title', label);
                      toast.success(tr ? `Ürün adı "${label}" olarak güncellendi` : `Product name updated to "${label}"`);
                    }}
                    disabled={!form.forklift_tonnage}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold transition-all shadow-sm">
                    <Check className="h-3.5 w-3.5" /> Uygula
                  </button>
                </div>
                {form.forklift_tonnage && (
                  <p className="text-xs text-blue-500 bg-blue-50 px-3 py-1.5 rounded-lg">
                    Önizleme: <span className="font-semibold">Özel Forklift {form.forklift_tonnage} Ton</span>
                    <span className="text-gray-400 ml-1">— Ürün adına uygulamak için "Uygula"ya tıklayın</span>
                  </p>
                )}
              </div>
            )}
            {/* Operating hours */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Çalışma Saatleri</Label>
              <div className="flex items-center gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-400">Başlangıç</Label>
                  <Input type="time" value={form.operating_hours_start || '08:00'}
                    onChange={e => set('operating_hours_start', e.target.value)}
                    data-testid="op-hours-start" className="rounded-xl w-32" />
                </div>
                <span className="text-gray-300 pt-5">—</span>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-400">Bitiş</Label>
                  <Input type="time" value={form.operating_hours_end || '18:00'}
                    onChange={e => set('operating_hours_end', e.target.value)}
                    data-testid="op-hours-end" className="rounded-xl w-32" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2 - Temel Bilgiler (Kategori ve spesifik ürün seçiminden sonra) */}
        <div className="p-6 space-y-4">
          <SectionHeader step="2" title="Temel Bilgiler" desc={
            isForklift && form.forklift_type ? 'Seçilen forklift tipine göre otomatik dolduruldu, değiştirebilirsiniz.' : undefined
          } />
          <div className="grid grid-cols-2 gap-4">
            {/* Ürün Adı — TR + EN */}
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇹🇷</span> {isService ? 'Hizmet Adı (TR)' : 'Ürün Adı (TR)'} <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.title_tr}
                  onChange={e => { set('title_tr', e.target.value); set('title', e.target.value || form.title_en); }}
                  data-testid="product-title-tr-input"
                  className={`rounded-xl ${errors.title ? 'border-red-400' : ''}`}
                  placeholder="Türkçe ürün adı..."
                />
                {errors.title && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.title}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇬🇧</span> {isService ? 'Service Name (EN)' : 'Product Name (EN)'}
                </Label>
                <Input
                  value={form.title_en}
                  onChange={e => { set('title_en', e.target.value); if (!form.title_tr) set('title', e.target.value); }}
                  data-testid="product-title-en-input"
                  className="rounded-xl"
                  placeholder="English product name..."
                />
              </div>
            </div>
            <div className="col-span-2 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇹🇷</span> Açıklama (Türkçe)
                </Label>
                <textarea rows={4} value={form.description_tr} onChange={e => set('description_tr', e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                  placeholder="Türkçe ürün açıklaması... (satır sonları korunur)" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇬🇧</span> Description (English)
                </Label>
                <textarea rows={4} value={form.description_en} onChange={e => set('description_en', e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                  placeholder="English product description... (line breaks preserved)" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                <Tag className="h-3 w-3" /> Ürün Kodu (opsiyonel)
              </Label>
              <Input value={form.product_code} onChange={e => set('product_code', e.target.value)}
                data-testid="product-code-input" className="rounded-xl font-mono"
                placeholder="Boş bırakın — otomatik (YK-XXXX)" />
            </div>
            {isRental && !isForklift && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Min. Kiralama Süresi (gün)</Label>
                <Input type="number" value={form.min_rental_period_days}
                  onChange={e => set('min_rental_period_days', e.target.value)}
                  className="rounded-xl" min="1" placeholder="—" />
              </div>
            )}
          </div>

          {/* Renk/Desen Seçimi — şablon için atanmış pattern öğeleri */}
          {availablePatternItems.length > 0 && (
            <div className="space-y-2 pt-1 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1.5">
                  <Palette className="h-3 w-3 text-purple-500" /> Renk/Desen Seçimi
                  <span className="text-[10px] font-normal text-gray-400 normal-case ml-1">Bu ürün için mevcut renkler</span>
                </Label>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedPatternIds(new Set(availablePatternItems.map(i => i.id)))}
                    className="text-[10px] text-purple-600 hover:text-purple-800">Tümünü Seç</button>
                  <span className="text-gray-300 text-[10px]">|</span>
                  <button onClick={() => setSelectedPatternIds(new Set())}
                    className="text-[10px] text-gray-400 hover:text-gray-600">Temizle</button>
                  {selectedPatternIds.size > 0 && (
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                      {selectedPatternIds.size} seçili
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {availablePatternItems.map(item => {
                  const sel = selectedPatternIds.has(item.id);
                  return (
                    <button
                      key={item.id} type="button" title={item.name}
                      onClick={() => setSelectedPatternIds(prev => { const n = new Set(prev); sel ? n.delete(item.id) : n.add(item.id); return n; })}
                      className={`group relative flex flex-col items-center gap-1 transition-all focus:outline-none`}
                    >
                      <div className={`w-10 h-10 rounded-xl overflow-hidden border-2 transition-all ${sel ? 'border-purple-500 ring-2 ring-purple-200 scale-110' : 'border-gray-200 hover:border-purple-300'}`}>
                        {item.image_url
                          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />}
                        {sel && <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center"><Check className="h-3.5 w-3.5 text-purple-700" strokeWidth={3} /></div>}
                      </div>
                      <span className={`text-[9px] max-w-[40px] truncate ${sel ? 'text-purple-700 font-semibold' : 'text-gray-400'}`}>{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* 3 - Pricing (conditional per product type) */}
        <div className="p-6 space-y-4">
          <SectionHeader
            step="3"
            title={isForklift ? 'Saatlik Ücret' : 'Fiyatlandırma'}
            desc={
              isForklift  ? 'Forklift için saatlik kiralama ücreti.' :
              isRental    ? 'Günlük ve/veya etkinlik fiyatı girin.' :
              isService   ? 'Hizmet fiyatı ve hesaplama tipini belirleyin.' :
                            'Birim satış fiyatını girin.'
            }
          />
          {errors.pricing && (
            <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl">
              <AlertCircle className="h-4 w-4 shrink-0" />{errors.pricing}
            </div>
          )}

          {/* Common: Currency + Lead time + Active toggle */}
          <div className="flex items-center gap-3">
            <div className="space-y-1.5 w-32">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Para Birimi</Label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)} className={SEL} data-testid="currency-select">
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5 flex-1">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Tedarik Süresi</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="same-day-toggle"
                  onClick={() => set('lead_time_days', form.lead_time_days === '0' ? '' : '0')}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all whitespace-nowrap ${
                    form.lead_time_days === '0'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-600'
                  }`}
                >
                  Aynı Gün
                </button>
                <Input
                  type="number"
                  value={form.lead_time_days === '0' ? '' : form.lead_time_days}
                  onChange={e => set('lead_time_days', e.target.value)}
                  disabled={form.lead_time_days === '0'}
                  placeholder="Gün sayısı"
                  className="rounded-xl max-w-[200px] disabled:opacity-40"
                  min="1"
                  data-testid="lead-time-input"
                />
                {form.lead_time_days && form.lead_time_days !== '0' && (
                  <span className="text-xs text-gray-400 whitespace-nowrap">gün</span>
                )}
              </div>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
            <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                  data-testid="product-active-toggle"
                  className={`w-11 h-6 rounded-full transition-colors relative ${form.is_active ? 'bg-emerald-500' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-all ${form.is_active ? 'left-5' : 'left-0.5'}`} />
                </button>
                <span className="text-sm text-gray-600">Aktif</span>
              </label>
            </div>
          </div>

          {/* ── Manual review toggle ──────────────────────────────────────── */}
          {/* Stock catalog items typically ship as soon as the customer pays;
              flip this ON for made-to-order builds, custom services, etc.
              where you want to review the order before it's confirmed. */}
          <div className="bg-amber-50/60 rounded-2xl p-4 flex items-start gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, requires_supplier_confirmation: !f.requires_supplier_confirmation }))}
              data-testid="product-requires-confirmation-toggle"
              className={`w-11 h-6 rounded-full transition-colors relative shrink-0 mt-0.5 ${form.requires_supplier_confirmation ? 'bg-amber-500' : 'bg-gray-200'}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-all ${form.requires_supplier_confirmation ? 'left-5' : 'left-0.5'}`} />
            </button>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                Manuel onay gerektirsin
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {form.requires_supplier_confirmation
                  ? 'Müşteri ödeme yapsa bile sipariş "Onay Bekliyor" olarak kalır — siz manuel olarak onaylayana kadar. Özel tasarım/imalat gerektiren ürünler için uygundur.'
                  : 'Ödeme yapıldığı anda sipariş otomatik onaylanır ve "Ödendi" olarak işaretlenir. Standart katalog ürünleri için önerilir.'}
              </div>
            </div>
          </div>

          {/* ── FORKLIFT HOURLY PRICE ── */}
          {isForklift && (
            <div className="bg-blue-50/60 rounded-2xl p-4 space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Saatlik Ücret</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                <Input type="number" value={form.hourly_price}
                  onChange={e => set('hourly_price', e.target.value)}
                  data-testid="hourly-price-input"
                  className="rounded-xl pl-12" min="0" placeholder="0.00" />
              </div>
            </div>
          )}

          {/* ── RENTAL PRICING ── */}
          {!isForklift && isRental && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50/60 rounded-2xl p-4 space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">Günlük Kiralama Fiyatı</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                    <Input type="number" value={form.daily_price} onChange={e => set('daily_price', e.target.value)}
                      data-testid="create-daily-price" className="rounded-xl pl-12" min="0" placeholder="0.00" />
                  </div>
                  <p className="text-[10px] text-emerald-600">Müşteri tarihleri seçer → gün × fiyat</p>
                </div>
                <div className="bg-amber-50/60 rounded-2xl p-4 space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">Etkinlik Fiyatı (Sabit)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                    <Input type="number" value={form.event_price} onChange={e => set('event_price', e.target.value)}
                      data-testid="create-event-price" className="rounded-xl pl-12" min="0" placeholder="0.00" />
                  </div>
                  <p className="text-[10px] text-amber-600">Tüm etkinlik boyunca tek fiyat</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hesaplama Tipi</Label>
                {appliedProfile && (
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
                    <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                    <span className="text-xs text-indigo-700">
                      <span className="font-semibold">"{appliedProfile.name}"</span> profilinden otomatik ayarlandı
                      {appliedProfile.formula && (
                        <span className="ml-1 text-indigo-500">— {appliedProfile.formula}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className={`grid grid-cols-1 gap-2 ${appliedProfile ? 'pointer-events-none opacity-60' : ''}`}>
                  {[
                    {key:'flat',             label:'Sabit (flat)',   desc:'Direkt fiyat, adet/kişi/gün gerekmez'},
                    {key:'quantity_based',   label:'Adet Bazlı',     desc:'Adet × fiyat'},
                    {key:'person_based',     label:'Kişi Bazlı',     desc:'Kişi sayısı × fiyat'},
                    {key:'quantity_and_days',label:'Adet × Gün',    desc:'Adet × gün × fiyat (etkinlikte: adet × fiyat)'},
                    {key:'person_and_days',  label:'Kişi × Gün',    desc:'Kişi × gün × fiyat (etkinlikte: kişi × fiyat)'},
                  ].map(opt => (
                    <button key={opt.key} onClick={() => set('calculation_type', opt.key)}
                      className={`rounded-xl px-3 py-2 text-left text-sm border-2 transition-all ${form.calculation_type===opt.key?'border-amber-500 bg-amber-50':'border-gray-100 hover:border-gray-200'}`}>
                      <span className={`font-semibold ${form.calculation_type===opt.key?'text-amber-700':'text-gray-700'}`}>{opt.label}</span>
                      <span className="text-xs text-gray-400 ml-2">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              {(form.calculation_type === 'person_based' || form.calculation_type === 'person_and_days') && (
                <div className="bg-purple-50/50 rounded-2xl p-4 space-y-3 border border-purple-100">
                  <p className="text-xs font-semibold text-purple-600 uppercase">Catering Kişi Bazlı Fiyatlar</p>
                  <div className="grid grid-cols-2 gap-3">
                    {(form.pricing_mode_supported==='daily'||form.pricing_mode_supported==='both') && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-500 uppercase">Kişi Başı / Gün</Label>
                        <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{form.currency}</span><Input type="number" value={form.price_per_person_per_day} onChange={e=>set('price_per_person_per_day',e.target.value)} className="rounded-xl pl-10" min="0" placeholder="0.00" /></div>
                      </div>
                    )}
                    {(form.pricing_mode_supported==='event'||form.pricing_mode_supported==='both') && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-500 uppercase">Kişi Başı / Etkinlik</Label>
                        <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{form.currency}</span><Input type="number" value={form.price_per_person_event} onChange={e=>set('price_per_person_event',e.target.value)} className="rounded-xl pl-10" min="0" placeholder="0.00" /></div>
                      </div>
                    )}
                    <div className="space-y-1.5"><Label className="text-xs text-gray-500 uppercase">Min. Kişi</Label><Input type="number" value={form.minimum_people} onChange={e=>set('minimum_people',e.target.value)} className="rounded-xl" min="1" placeholder="1" /></div>
                    <div className="space-y-1.5"><Label className="text-xs text-gray-500 uppercase">Maks. Kişi</Label><Input type="number" value={form.maximum_people} onChange={e=>set('maximum_people',e.target.value)} className="rounded-xl" min="1" placeholder="—" /></div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── SERVICE PRICING ── */}
          {!isForklift && isService && (
            <>
              {/* HR kategorisi: sadece Kişi Başı göster */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hizmet Fiyatlandırma Tipi</Label>
                {isHR ? (
                  <div>
                    <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-xl border-2 border-purple-500 bg-purple-50 text-purple-700">
                      Kişi Başı
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {[
                      {key:'fixed',  label:'Sabit Ücret'},
                      {key:'hourly', label:'Saatlik'},
                      {key:'daily',  label:'Günlük'},
                      {key:'person', label:'Kişi Başı'},
                      {key:'custom', label:'Özel'},
                    ].map(opt => (
                      <button key={opt.key} onClick={() => set('service_pricing_type', opt.key)}
                        className={`rounded-xl px-3 py-1.5 text-xs font-semibold border-2 transition-all ${form.service_pricing_type===opt.key?'border-purple-500 bg-purple-50 text-purple-700':'border-gray-100 text-gray-500 hover:border-gray-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Günlük VEYA HR (Kişi Başı) → iki fiyat yan yana */}
              {(form.service_pricing_type === 'daily' || isHR) ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-50/60 rounded-2xl p-4 space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase">
                      {isHR ? 'Günlük Fiyat (Kişi Başı)' : 'Günlük Fiyat'}
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                      <Input type="number" value={form.daily_price} onChange={e => set('daily_price', e.target.value)}
                        data-testid="create-daily-price"
                        className="rounded-xl pl-12" min="0" placeholder="0.00" />
                    </div>
                    <p className="text-[10px] text-emerald-600">
                      {isHR ? 'Kişi sayısı × günlük fiyat × gün' : 'Müşteri tarihleri seçer → gün × fiyat'}
                    </p>
                  </div>
                  <div className="bg-amber-50/60 rounded-2xl p-4 space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase">Etkinlik Fiyatı (Sabit)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                      <Input type="number" value={form.event_price} onChange={e => set('event_price', e.target.value)}
                        data-testid="create-event-price"
                        className="rounded-xl pl-12" min="0" placeholder="0.00" />
                    </div>
                    <p className="text-[10px] text-amber-600">Tüm etkinlik boyunca tek fiyat</p>
                  </div>
                </div>
              ) : (
                <div className="bg-purple-50/50 rounded-2xl p-4 space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">Hizmet Fiyatı <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                    <Input type="number" value={form.daily_price} onChange={e => set('daily_price', e.target.value)}
                      data-testid="create-service-price"
                      className="rounded-xl pl-12" min="0" placeholder="0.00" />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hesaplama Tipi</Label>
                {isHR ? (
                  <div>
                    <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-xl border-2 border-purple-500 bg-purple-50 text-purple-700">
                      Kişi Bazlı
                    </span>
                  </div>
                ) : (
                  <>
                    {appliedProfile && (
                      <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
                        <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                        <span className="text-xs text-indigo-700">
                          <span className="font-semibold">"{appliedProfile.name}"</span> profilinden otomatik ayarlandı
                          {appliedProfile.formula && (
                            <span className="ml-1 text-indigo-500">— {appliedProfile.formula}</span>
                          )}
                        </span>
                      </div>
                    )}
                    <div className={`grid grid-cols-1 gap-2 ${appliedProfile ? 'pointer-events-none opacity-60' : ''}`}>
                      {[
                        {key:'flat',          label:'Sabit',        desc:'Tek seferlik sabit ücret'},
                        {key:'quantity_based',label:'Adet Bazlı',   desc:'Adet × fiyat'},
                        {key:'person_based',  label:'Kişi Bazlı',   desc:'Kişi sayısı × fiyat'},
                      ].map(opt => (
                        <button key={opt.key} onClick={() => set('calculation_type', opt.key)}
                          className={`rounded-xl px-3 py-2 text-left text-sm border-2 transition-all ${form.calculation_type===opt.key?'border-purple-500 bg-purple-50':'border-gray-100 hover:border-gray-200'}`}>
                          <span className={`font-semibold ${form.calculation_type===opt.key?'text-purple-700':'text-gray-700'}`}>{opt.label}</span>
                          <span className="text-xs text-gray-400 ml-2">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {(form.calculation_type === 'person_based' || isHR) && (
                <div className="bg-purple-50/50 rounded-2xl p-4 space-y-3 border border-purple-100">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs text-gray-500 uppercase">Min. Kişi</Label><Input type="number" value={form.minimum_people} onChange={e=>set('minimum_people',e.target.value)} className="rounded-xl" min="1" placeholder="1" /></div>
                    <div className="space-y-1.5"><Label className="text-xs text-gray-500 uppercase">Maks. Kişi</Label><Input type="number" value={form.maximum_people} onChange={e=>set('maximum_people',e.target.value)} className="rounded-xl" min="1" placeholder="—" /></div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── FOR SALE PRICING ── */}
          {!isForklift && isForSale && (
            <>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hesaplama Tipi</Label>
                {appliedProfile && (
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
                    <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                    <span className="text-xs text-indigo-700">
                      <span className="font-semibold">"{appliedProfile.name}"</span> profilinden otomatik ayarlandı
                      {appliedProfile.formula && (
                        <span className="ml-1 text-indigo-500">— {appliedProfile.formula}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className={`grid grid-cols-1 gap-2 ${appliedProfile ? 'pointer-events-none opacity-60' : ''}`}>
                  {[
                    {key:'quantity_based', label:'Adet Bazlı',      desc:'Adet × birim fiyat'},
                    {key:'carton_based',   label:'Koli/Paket Bazlı', desc:'Koli sayısı × koli fiyatı'},
                  ].map(opt => (
                    <button key={opt.key} onClick={() => set('calculation_type', opt.key)}
                      className={`rounded-xl px-3 py-2 text-left text-sm border-2 transition-all ${form.calculation_type===opt.key?'border-blue-500 bg-blue-50':'border-gray-100 hover:border-gray-200'}`}>
                      <span className={`font-semibold ${form.calculation_type===opt.key?'text-blue-700':'text-gray-700'}`}>{opt.label}</span>
                      <span className="text-xs text-gray-400 ml-2">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Carton: items per carton first */}
              {form.calculation_type === 'carton_based' && (
                <div className="bg-orange-50/60 rounded-2xl p-4 space-y-1.5 border border-orange-100">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">
                    Bir Koli / Pakette Kaç Adet Var? <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    value={form.items_per_carton}
                    onChange={e => set('items_per_carton', e.target.value)}
                    data-testid="create-items-per-carton"
                    className="rounded-xl" min="1" placeholder="Örn: 12"
                  />
                  <p className="text-xs text-gray-400">Her bir koli/pakette bulunan birim sayısı (ör: 12 şişe/koli)</p>
                </div>
              )}

              <div className={`rounded-2xl p-4 space-y-1.5 border ${form.calculation_type === 'carton_based' ? 'bg-orange-50/30 border-orange-100' : 'bg-blue-50/50 border-blue-100'}`}>
                <Label className="text-xs font-semibold text-gray-500 uppercase">
                  {form.calculation_type === 'carton_based' ? 'Koli / Paket Fiyatı' : 'Satış Fiyatı (Birim)'} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                  <Input type="number" value={form.daily_price} onChange={e => set('daily_price', e.target.value)}
                    data-testid="create-sale-price"
                    className="rounded-xl pl-12" min="0" placeholder="0.00" />
                </div>
                {form.calculation_type === 'carton_based' && (
                  <p className="text-xs text-gray-400">Her koli/paket için toplam fiyat. Birim başına fiyat = Koli fiyatı ÷ koli adedi</p>
                ) }
                {form.calculation_type === 'quantity_based' && (
                  <p className="text-xs text-gray-400">Birim satış fiyatı. Müşteri adet girerek toplam hesaplanır.</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* 4 - Stock Management */}
        <div className="p-6 space-y-4">
          <SectionHeader step="4" title="Stok Yönetimi" desc="Ürün uygunluğunun nasıl hesaplanacağını belirleyin." />
          <div className="space-y-2">
            {STOCK_POLICIES.map(pol => (
              <label key={pol.value}
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  stockPolicy === pol.value ? 'border-amber-400 bg-amber-50' : 'border-gray-100 hover:border-gray-200'
                }`}>
                <input type="radio" name="stock_policy" value={pol.value}
                  data-testid={`stock-policy-${pol.value}`}
                  checked={stockPolicy === pol.value}
                  onChange={() => setStockPolicy(pol.value)}
                  className="mt-0.5 accent-amber-500" />
                <div>
                  <span className="text-sm font-semibold text-gray-800">{pol.label}</span>
                  <p className="text-xs text-gray-400">{pol.desc}</p>
                </div>
              </label>
            ))}
          </div>
          {stockPolicy !== 'untracked' && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase">
                  {isForklift ? 'Filo Büyüklüğü (Araç Sayısı)' : 'Stok Miktarı'}
                </Label>
                <Input type="number" value={stockQuantity} onChange={e => setStockQuantity(e.target.value)}
                  data-testid="stock-quantity-input"
                  className="rounded-xl" min="0" placeholder={isForklift ? '5' : '0'} />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button type="button" onClick={() => setAllowBackorder(!allowBackorder)}
                    data-testid="allow-backorder-toggle"
                    className={`w-11 h-6 rounded-full transition-colors relative ${allowBackorder ? 'bg-amber-500' : 'bg-gray-200'}`}>
                    <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform ${allowBackorder ? 'translate-x-5.5 left-0.5' : 'left-0.5'}`} />
                  </button>
                  <span className="text-sm text-gray-600">Ön sipariş izni</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* 5 - Images */}
        <div className="p-6 space-y-4">
          <SectionHeader step="5" title="Görseller" desc="Maks. 5 görsel, 10 MB/adet. İlk görsel otomatik kapak olur." />
          {pendingImages.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {pendingImages.map(item => (
                <PendingImage
                  key={item.id}
                  preview={item.preview}
                  isCover={item.isCover}
                  onSetCover={() => setPendingImages(prev => prev.map(p => ({ ...p, isCover: p.id === item.id })))}
                  onRemove={() => {
                    setPendingImages(prev => {
                      const next = prev.filter(p => p.id !== item.id);
                      if (item.isCover && next.length > 0) next[0] = { ...next[0], isCover: true };
                      return next;
                    });
                  }}
                />
              ))}
              {pendingImages.length < 5 && (
                <button onClick={() => fileRef.current?.click()}
                  className="h-24 w-24 rounded-xl border-2 border-dashed border-amber-200 flex items-center justify-center text-amber-400 hover:bg-amber-50 transition-colors">
                  <Upload className="h-6 w-6" />
                </button>
              )}
            </div>
          )}
          {pendingImages.length === 0 && (
            <div ref={dropRef}
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 transition-colors">
              <Upload className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500">Görselleri sürükleyin veya tıklayın</p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP · Maks. 10 MB/görsel · En fazla 5 görsel</p>
              <div className="mt-3 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <span className="text-[11px] text-amber-700 font-medium">Önerilen boyut: 800×800 px (kare, 1:1 oran)</span>
              </div>
            </div>
          )}
          <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
            onChange={e => { addImages(e.target.files); e.target.value = ''; }} />
        </div>

        {/* 6 - Variants */}
        <div className="p-6 space-y-4">
          <SectionHeader step="6" title="Varyantlar (Opsiyonel)"
            desc="Farklı renk veya boyut seçenekleri ekleyin. Fiyat ve stok ana üründen otomatik gelir." />

          {variants.length > 0 && (
            <div className="space-y-3">
              {variants.map((v, i) => {
                const colorItem = v.color_id ? availablePatternItems.find(p => p.id === v.color_id) : null;
                return (
                  <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl p-4 space-y-3">
                    {/* Row 1: Badge + Color Swatch + Name */}
                    <div className="flex items-center gap-3">
                      <span className="h-7 w-7 rounded-lg bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>

                      {/* Color picker — from ALL template pattern items */}
                      {availablePatternItems.length > 0 && (
                        <div className="relative">
                          <button type="button"
                            onClick={() => setOpenColorPicker(openColorPicker === i ? null : i)}
                            className={`h-9 w-9 rounded-xl border-2 overflow-hidden transition-all flex items-center justify-center ${colorItem ? 'border-purple-400' : 'border-dashed border-gray-300 hover:border-purple-300'}`}
                            title={colorItem ? colorItem.name : 'Renk seç'}>
                            {colorItem?.image_url
                              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${colorItem.image_url}`} className="w-full h-full object-cover" alt="" />
                              : <Palette className="h-4 w-4 text-gray-300" />}
                          </button>
                          {/* Color dropdown — state driven, shows ALL available items */}
                          {openColorPicker === i && (
                            <div className="absolute left-0 top-11 z-30 bg-white rounded-2xl shadow-xl border border-gray-100 p-3 flex flex-wrap gap-1.5 w-52">
                              <button type="button" onClick={() => { setVariants(p => p.map((x, j) => j === i ? { ...x, color_id: null } : x)); setOpenColorPicker(null); }}
                                className="text-[10px] text-gray-400 hover:text-red-500 w-full text-left px-1 pb-2 border-b border-gray-100 mb-1">Rengi Kaldır</button>
                              {availablePatternItems.map(ci => (
                                <button key={ci.id} type="button" title={ci.name}
                                  onClick={() => { setVariants(p => p.map((x, j) => j === i ? { ...x, color_id: ci.id, name: ci.name } : x)); setOpenColorPicker(null); }}
                                  className={`h-9 w-9 rounded-lg overflow-hidden border-2 transition-all ${v.color_id === ci.id ? 'border-purple-500 scale-110' : 'border-gray-200 hover:border-purple-300'}`}>
                                  {ci.image_url
                                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}${ci.image_url}`} className="w-full h-full object-cover" alt={ci.name} />
                                    : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><span className="text-[8px] text-gray-400">{ci.name?.slice(0,3)}</span></div>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <Input value={v.name}
                        onChange={e => setVariants(p => p.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                        placeholder="Varyant adı (ör: Mavi, XL...)" className="rounded-xl flex-1" />

                      {/* Variant image upload */}
                      <label className="cursor-pointer flex-shrink-0">
                        <div className={`h-9 w-9 rounded-xl border-2 border-dashed overflow-hidden flex items-center justify-center transition-all ${v.image_preview ? 'border-amber-400' : 'border-gray-200 hover:border-amber-300'}`}>
                          {v.image_preview
                            ? <img src={v.image_preview} className="w-full h-full object-cover" alt="" />
                            : <ImageIcon className="h-4 w-4 text-gray-300" />}
                        </div>
                        <input type="file" accept="image/*" className="sr-only"
                          ref={el => { variantFileRefs.current[i] = el; }}
                          onChange={e => {
                            const file = e.target.files?.[0]; if (!file) return;
                            setVariants(p => p.map((x, j) => j === i ? { ...x, image_file: file, image_preview: URL.createObjectURL(file) } : x));
                            e.target.value = '';
                          }} />
                      </label>

                      <button onClick={() => setVariants(p => p.filter((_, j) => j !== i))}
                        className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0">
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Row 2: Price(s) + Stock */}
                    <div className="grid grid-cols-3 gap-2 pl-10">
                      {/* Pricing — matches main product type */}
                      {(isRental || (isService && form.service_pricing_type === 'daily')) ? (
                        <>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-400 uppercase font-semibold">Günlük Fiyat</label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{form.currency}</span>
                              <Input type="number" value={v.daily_price}
                                onChange={e => setVariants(p => p.map((x, j) => j === i ? { ...x, daily_price: e.target.value } : x))}
                                className="rounded-lg pl-8 text-sm h-8" placeholder="0.00" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-400 uppercase font-semibold">Etkinlik Fiyatı</label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{form.currency}</span>
                              <Input type="number" value={v.event_price}
                                onChange={e => setVariants(p => p.map((x, j) => j === i ? { ...x, event_price: e.target.value } : x))}
                                className="rounded-lg pl-8 text-sm h-8" placeholder="0.00" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="space-y-1 col-span-2">
                          <label className="text-[10px] text-gray-400 uppercase font-semibold">Fiyat</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{form.currency}</span>
                            <Input type="number" value={v.daily_price}
                              onChange={e => setVariants(p => p.map((x, j) => j === i ? { ...x, daily_price: e.target.value } : x))}
                              className="rounded-lg pl-8 text-sm h-8" placeholder="0.00" />
                          </div>
                        </div>
                      )}
                      <div className="space-y-1">
                        <label className="text-[10px] text-gray-400 uppercase font-semibold">Stok</label>
                        <Input type="number" value={v.stock}
                          onChange={e => setVariants(p => p.map((x, j) => j === i ? { ...x, stock: e.target.value } : x))}
                          className="rounded-lg text-sm h-8" min="0" placeholder="0" />
                      </div>
                    </div>

                    {/* Row 3: Auto code (readonly) + Color label */}
                    <div className="flex items-center gap-2 pl-10">
                      <span className="text-[10px] font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                        YK-XXXX-V{String(i + 1).padStart(2, '0')}
                      </span>
                      {colorItem && (
                        <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <Palette className="h-2.5 w-2.5" />{colorItem.name}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add variant buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => {
              const baseDaily = form.daily_price || '';
              const baseEvent = form.event_price || '';
              const baseStock = form.min_order_quantity || '';
              setVariants(prev => [...prev, { name: '', daily_price: baseDaily, event_price: baseEvent, stock: baseStock, color_id: null, image_file: null, image_preview: null }]);
            }}
              data-testid="add-variant-btn"
              className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1.5">
              <span className="text-lg leading-none">+</span> Varyant Ekle
            </button>
            {selectedPatternIds.size > 0 && (
              <button
                type="button"
                onClick={() => {
                  const baseDaily = form.daily_price || '';
                  const baseEvent = form.event_price || '';
                  const baseStock = form.min_order_quantity || '';
                  const colorVariants = [...selectedPatternIds].map(cid => {
                    const item = availablePatternItems.find(p => p.id === cid);
                    return { name: item?.name || '', daily_price: baseDaily, event_price: baseEvent, stock: baseStock, color_id: cid, image_file: null, image_preview: null };
                  });
                  setVariants(prev => [...prev, ...colorVariants]);
                  setOpenColorPicker(null);
                }}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1.5 border border-purple-200 rounded-xl px-3 py-1.5 hover:bg-purple-50 transition-colors"
              >
                <Palette className="h-3.5 w-3.5" />
                Seçili renklerden varyant oluştur ({selectedPatternIds.size})
              </button>
            )}
            {availablePatternItems.length > 0 && selectedPatternIds.size === 0 && (
              <button
                type="button"
                onClick={() => {
                  const baseDaily = form.daily_price || '';
                  const baseEvent = form.event_price || '';
                  const baseStock = form.min_order_quantity || '';
                  const colorVariants = availablePatternItems.map(item => ({
                    name: item.name, daily_price: baseDaily, event_price: baseEvent, stock: baseStock, color_id: item.id, image_file: null, image_preview: null
                  }));
                  setVariants(prev => [...prev, ...colorVariants]);
                  setOpenColorPicker(null);
                }}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1.5 border border-purple-200 rounded-xl px-3 py-1.5 hover:bg-purple-50 transition-colors"
              >
                <Palette className="h-3.5 w-3.5" />
                Tüm renklerden varyant oluştur ({availablePatternItems.length})
              </button>
            )}
          </div>
        </div>

        {uploadState && (
          <div className="p-6"><UploadProgress {...uploadState} /></div>
        )}

        <div className="p-6 flex gap-3">
          <Button
            data-testid="product-create-submit"
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-12 text-base font-semibold gap-2">
            {saving
              ? <><Loader2 className="h-5 w-5 animate-spin" />Kaydediliyor...</>
              : <><Check className="h-5 w-5" />Ürünü Kaydet</>}
          </Button>
          <Button variant="outline" onClick={() => setProductType(null)} className="rounded-xl px-6">Geri</Button>
        </div>
      </div>
    </div>
  );
}
